import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { MassOperationResponse } from './company-api.service';
import { LoadSuccessParams } from 'ag-grid-community';
import {
  UTILS_DATA_OBJECT_GET_LIST_OUTPUT_TEMPLATES,
  UTILS_DATA_OBJECT_GET_OBJECT,
  UTILS_DATA_OBJECT_POST_SET_DIMENSIONS,
  UTILS_DATA_OBJECT_POST_DETACH_DIMENSION,
  UTILS_DATA_OBJECT_GET_LIST_COMPANIES,
  UTILS_DATA_OBJECT_POST_LIST_PRINCIPALS,
  UTILS_DATA_OBJECT_GET_LIST_CONNECTORS,
  UTILS_DATA_OBJECT_GET_LIST_OUTPUT_SETTINGS,
  UTILS_DATA_OBJECT_POST_LIST_PRINCIPALS_BY_IDS,
  UTILS_DATA_OBJECT_POST_MOVE_OBJECTS,
  UTILS_DATA_OBJECT_POST_SCHEDULE,
  UTILS_DATA_OBJECT_POST_TOGGLE_DIMENSION_JOIN_TYPE,
  UTILS_DATA_OBJECT_POST_SHARE,
  UTILS_DATA_OBJECT_POST_TOGGLE_PERIOD_LOCKED,
  UTILS_DATA_OBJECT_POST_USED_BY,
} from '../endpoints/utils-data-object.endpoints';

import { toQueryParams } from '../api-utils';

import {
  Connector,
  ContentFilters,
  DataObject,
  Dimension,
  ExportSchedule,
  NomItem,
  ObjectDefinition,
} from 'src/app/core/models/core.models';
import {
  DimensionAttachTypeId,
  DimensionJoinTypeId,
  NomObjectClass,
} from 'src/app/core/constants';

import { Observable } from 'rxjs';
import {
  OutputSettings,
  OutputTemplate,
} from 'src/app/core/models/output-settings.models';
import {
  PrincipalListResponse,
  ShareResponse,
  ShareSettings,
} from 'src/app/core/models/object.models';

export interface AttachDimensionsResponse {
  dimensions: Array<Dimension>;
  query: string;
}

/** Output templates keyed by output format id. */
export interface TemplateResponse {
  [key: string]: OutputTemplate[];
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class UtilsDataObjectApiService {
  private readonly http = inject(HttpClient);

  /**
   * Fetches a data object from the backend by its ID and class ID.
   */
  fetchObject(
    objectId: string,
    objectClassId: number,
    listVerison: boolean = true,
  ): Observable<DataObject> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
      list_version: listVerison,
    });
    return this.http.get<DataObject>(UTILS_DATA_OBJECT_GET_OBJECT, { params });
  }

  getConnectors(companyId: string): Observable<Connector[]> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http.get<Connector[]>(UTILS_DATA_OBJECT_GET_LIST_CONNECTORS, {
      params,
    });
  }

  getCompanies(): Observable<NomItem[]> {
    return this.http.get<NomItem[]>(UTILS_DATA_OBJECT_GET_LIST_COMPANIES);
  }

  getOutputTemplates(): Observable<TemplateResponse> {
    return this.http.get<TemplateResponse>(
      UTILS_DATA_OBJECT_GET_LIST_OUTPUT_TEMPLATES,
    );
  }

  getOutputSettingsList(
    objectClassId: number,
    objectId: string,
  ): Observable<Array<OutputSettings>> {
    // ! missing filters are sent as the literal string "null" (Angular stringifies
    // ! the value), which is what the backend expects for the unfiltered list
    const params = {
      object_class_id: objectClassId?.toString() ?? null,
      object_id: objectId ?? null,
    } as any;

    return this.http.get<Array<OutputSettings>>(
      UTILS_DATA_OBJECT_GET_LIST_OUTPUT_SETTINGS,
      { params },
    );
  }

  /** Fetches the principals of a company, paged and optionally filtered. */
  fetchCompanyPrincipals(
    companyId: string,
    page: number,
    searchTerm?: string,
    objectClassId: number[] = [NomObjectClass.USER, NomObjectClass.USER_GROUP],
    lockedUserIds: string[] = [],
  ): Observable<PrincipalListResponse> {
    // locked_user_ids: principals the backend leaves out of the list (it
    // honours the key on both list_principals implementations) - the owner
    // flow passes the current owner so only that account is excluded (WAP-1660).
    const params = {
      company_id: companyId,
      page: page,
      query: searchTerm || '',
      object_class_id: objectClassId || [],
      ...(lockedUserIds.length > 0 ? { locked_user_ids: lockedUserIds } : {}),
    };
    return this.http.post<PrincipalListResponse>(
      UTILS_DATA_OBJECT_POST_LIST_PRINCIPALS,
      params,
    );
  }

  getPrincipalsByIds(
    objects: ObjectDefinition[],
  ): Observable<PrincipalListResponse> {
    const params = {
      objects,
    };
    return this.http.post<PrincipalListResponse>(
      UTILS_DATA_OBJECT_POST_LIST_PRINCIPALS_BY_IDS,
      params,
    );
  }

  fetchUsedByObjectsAgGrid(
    objectId,
    objectClassId,
    gridParams,
  ): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({
      output: 'table',
      object_id: objectId,
      object_class_id: objectClassId,
    });

    return this.http.post<LoadSuccessParams>(
      UTILS_DATA_OBJECT_POST_USED_BY,
      gridParams,
      { params: httpParams },
    );
  }

  moveObjects(
    objects: ObjectDefinition[],
    workspaceId: string,
  ): Observable<MassOperationResponse> {
    return this.http.post<MassOperationResponse>(
      UTILS_DATA_OBJECT_POST_MOVE_OBJECTS,
      { workspace_id: workspaceId, objects },
    );
  }

  // ! ─── A feed's attached dimensions ────────────────────────────────────────
  // ! `object_class_id` is always `FEED`: these three requests are only ever used
  // ! for a feed.

  /**
   * Replace a feed's attached-dimension set. Not additive: the caller sends the
   * full list it wants attached.
   */
  attachDimensions(
    feedId: string,
    dimensionIds: string[],
    attachTypeId: DimensionAttachTypeId,
  ): Observable<AttachDimensionsResponse> {
    return this.http.post<AttachDimensionsResponse>(
      UTILS_DATA_OBJECT_POST_SET_DIMENSIONS,
      {
        object_id: feedId,
        attach_type_id: attachTypeId,
        object_class_id: NomObjectClass.FEED,
        dimension_id: dimensionIds,
      },
    );
  }

  detachDimension(
    feedId: string,
    dimensionId: string,
  ): Observable<{ success: boolean; query: string }> {
    return this.http.post<{ success: boolean; query: string }>(
      UTILS_DATA_OBJECT_POST_DETACH_DIMENSION,
      {
        object_id: feedId,
        object_class_id: NomObjectClass.FEED,
        dimension_id: dimensionId,
      },
    );
  }

  /**
   * Create or update an export schedule — the backend decides which from
   * `export_schedule_id` inside `settings` (the form carries it when editing).
   */
  schedule(
    objectId: string,
    objectClassId: number,
    postIds: string[],
    contentFilters: ContentFilters,
    settings: Record<string, any>,
  ): Observable<ExportSchedule> {
    const params = {
      object_id: objectId,
      object_class_id: objectClassId,
      content_filters: contentFilters,
      post_ids: postIds,
      ...settings,
    };

    return this.http.post<ExportSchedule>(
      UTILS_DATA_OBJECT_POST_SCHEDULE,
      params,
    );
  }

  share(
    objectId: string,
    objectClassId: number,
    postIds: string[],
    contentFilters: ContentFilters,
    settings: ShareSettings,
    shareParams: any = {},
  ): Observable<ShareResponse> {
    const params = {
      object_id: objectId,
      object_class_id: objectClassId,
      content_filters: contentFilters,
      post_ids: postIds,
      ...settings,
      share_params: shareParams,
    };

    return this.http.post<ShareResponse>(UTILS_DATA_OBJECT_POST_SHARE, params);
  }

  /** Flip one attached dimension between AND and AND-NOT. */
  setDimensionJoinType(
    feedId: string,
    dimensionId: string,
    joinTypeId: DimensionJoinTypeId,
  ): Observable<{ query: string }> {
    return this.http.post<{ query: string }>(
      UTILS_DATA_OBJECT_POST_TOGGLE_DIMENSION_JOIN_TYPE,
      {
        dimension_id: dimensionId,
        join_type_id: joinTypeId,
        object_id: feedId,
        object_class_id: NomObjectClass.FEED,
      },
    );
  }

  togglePeriodLocked(
    objectId: string,
    objectClassId: number,
    isPeriodLocked: boolean,
    period: { startTimestamp: number; endTimestamp: number },
  ): Observable<any> {
    const params = {
      object_id: objectId,
      object_class_id: objectClassId,
      is_period_locked: isPeriodLocked,
      period: {
        start_timestamp: period.startTimestamp,
        end_timestamp: period.endTimestamp,
      },
    };

    return this.http.post<any>(
      UTILS_DATA_OBJECT_POST_TOGGLE_PERIOD_LOCKED,
      params,
    );
  }
}
